import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { DwErrorHeaders, DwSecurityUserService } from '@devwareapps/devware-cap';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppPermissions } from '../../../../meta-data/app-permissions.enum';
import {
  StructuredDocumentFavoriteModel,
  StructuredDocumentFavoriteResultModel,
  StructuredDocumentHierarchyModel,
  StructuredDocumentSectionModel,
  StructuredDocumentViewerModel
} from '../models/structured-document-viewer.model';
import { DocumentsRepositoryService } from '../../services/documents-repository.service';

@Injectable({ providedIn: 'root' })
export class StructuredDocumentViewerService {
  constructor(
    private http: HttpClient,
    private documentsRepositoryService: DocumentsRepositoryService,
    private dwSecurityUserService: DwSecurityUserService
  ) { }

  getStructuredDocumentViewer(documentItemId: number, documentVersionId?: number): Observable<StructuredDocumentViewerModel> {
    let headers = new HttpHeaders();
    headers = headers.append(DwErrorHeaders.suppressErrorMessagesHeader, 'true');
    const params = this.buildVersionParams(documentVersionId);

    return this.http.get<StructuredDocumentViewerModel>(
      `${this.documentsRepositoryService.documentsApi}/${documentItemId}/structured-viewer`,
      { headers, params }
    ).pipe(
      map(viewer => this.normalizeViewer(viewer))
    );
  }

  getStructuredDocumentSection(documentItemId: number, documentSectionId: number, documentVersionId?: number): Observable<StructuredDocumentSectionModel> {
    let headers = new HttpHeaders();
    headers = headers.append(DwErrorHeaders.suppressErrorMessagesHeader, 'true');
    const params = this.buildVersionParams(documentVersionId);

    return this.http.get<StructuredDocumentSectionModel>(
      `${this.documentsRepositoryService.documentsApi}/${documentItemId}/structured-sections/${documentSectionId}`,
      { headers, params }
    ).pipe(
      map(section => this.normalizeSection(section))
    );
  }

  getStructuredDocumentHierarchy(documentItemId: number, documentVersionId?: number, searchText?: string): Observable<StructuredDocumentHierarchyModel> {
    let headers = new HttpHeaders();
    headers = headers.append(DwErrorHeaders.suppressErrorMessagesHeader, 'true');
    let params = this.buildVersionParams(documentVersionId);

    if (searchText) {
      params = (params || new HttpParams()).set('searchText', searchText);
    }

    return this.http.get<StructuredDocumentHierarchyModel>(
      `${this.documentsRepositoryService.documentsApi}/${documentItemId}/structured-hierarchy`,
      { headers, params }
    );
  }

  getStructuredDocumentFavorites(): Observable<StructuredDocumentFavoriteModel[]> {
    let headers = new HttpHeaders();
    headers = headers.append(DwErrorHeaders.suppressErrorMessagesHeader, 'true');

    return this.http.get<StructuredDocumentFavoriteModel[]>(
      `${this.documentsRepositoryService.documentsApi}/structured-section-favorites`,
      { headers }
    );
  }

  addStructuredDocumentFavorite(
    documentItemId: number,
    documentSectionId: number,
    documentVersionId?: number
  ): Observable<StructuredDocumentFavoriteResultModel> {
    let headers = new HttpHeaders();
    headers = headers.append(DwErrorHeaders.suppressErrorMessagesHeader, 'true');

    return this.http.put<StructuredDocumentFavoriteResultModel>(
      `${this.documentsRepositoryService.documentsApi}/${documentItemId}/structured-sections/${documentSectionId}/favorite`,
      { documentVersionId },
      { headers }
    );
  }

  removeStructuredDocumentFavorite(userDocumentSectionFavoriteId: number): Observable<StructuredDocumentFavoriteResultModel> {
    let headers = new HttpHeaders();
    headers = headers.append(DwErrorHeaders.suppressErrorMessagesHeader, 'true');

    return this.http.put<StructuredDocumentFavoriteResultModel>(
      `${this.documentsRepositoryService.documentsApi}/structured-section-favorites/${userDocumentSectionFavoriteId}/inactive`,
      {},
      { headers }
    );
  }

  private buildVersionParams(documentVersionId?: number): HttpParams | undefined {
    if (!documentVersionId) {
      return undefined;
    }

    return new HttpParams().set('documentVersionId', `${documentVersionId}`);
  }

  private normalizeViewer(viewer: StructuredDocumentViewerModel): StructuredDocumentViewerModel {
    if (!viewer?.Sections?.length) {
      return viewer;
    }

    return {
      ...viewer,
      Sections: viewer.Sections.map(section => this.normalizeSection(section))
    };
  }

  private normalizeSection(section: StructuredDocumentSectionModel): StructuredDocumentSectionModel {
    if (!section) {
      return section;
    }

    return {
      ...section,
      Content: (section.Content || []).map(content => ({
        ...content,
        BlockJson: this.normalizeBlockJson(content?.BlockJson, section.DocumentSectionId, content?.DocumentSectionContentId)
      })),
      Anchors: section.Anchors || [],
      Assets: section.Assets || []
    };
  }

  private normalizeBlockJson(blockJson: any, documentSectionId?: number, documentSectionContentId?: number): any[] {
    if (Array.isArray(blockJson)) {
      return blockJson;
    }

    if (typeof blockJson === 'string') {
      try {
        const parsed = JSON.parse(blockJson);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        this.throwInvalidBlockJsonError(documentSectionId, documentSectionContentId, 'malformed JSON');
      }

      this.throwInvalidBlockJsonError(documentSectionId, documentSectionContentId, 'a non-array JSON value');
    }

    return [];
  }

  private throwInvalidBlockJsonError(
    documentSectionId: number | undefined,
    documentSectionContentId: number | undefined,
    reason: string
  ): never {
    const userMessage = 'We could not render this section content. Please try again later.';
    if (!this.dwSecurityUserService.hasPermission(AppPermissions.lMSAdminMenuAccess)) {
      throw new Error(userMessage);
    }

    const sectionIdentifier = documentSectionId ? ` for section ${documentSectionId}` : '';
    const contentIdentifier = documentSectionContentId ? ` (content ${documentSectionContentId})` : '';
    throw new Error(`${userMessage} Structured document content${sectionIdentifier}${contentIdentifier} contains ${reason}.`);
  }
}
